import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApp } from '../appContext';
import { useNavGuardConfirm } from '../unsavedChangesGuard';
import { TabBar, activeTabKey } from './registerTabs';
import { TeamsTab, TaskTypesTab, PeopleTab, FiscalCalendarTab, HolidaysTab } from './Configuration';
import { TemplatesTab, ProjectsTab, ModulesTab, BudgetCategoriesTab } from './ProjectConfiguration';
import { UsageTab, LimitsTab, PromptTab } from './AiSettings';
import ExternalUsers from './ExternalUsers';

// One entry per section, each carrying its own sub-tabs (or none, for Customer Access, which has
// nothing to split). This is the single place both levels' labels live -- the tree needs to draw
// them together, unlike the old per-page tab bars that only ever knew about their own level.
const TREE = [
  {
    key: 'people', label: 'People & Time',
    children: [
      { key: 'task-types', label: 'Timesheet Tasks', Body: TaskTypesTab },
      { key: 'teams', label: 'Teams', Body: TeamsTab },
      { key: 'people', label: 'People', Body: PeopleTab },
      { key: 'fiscal-calendar', label: 'Fiscal Calendar', Body: FiscalCalendarTab },
      { key: 'holidays', label: 'Company Holidays', Body: HolidaysTab },
    ],
  },
  {
    key: 'projects', label: 'Projects',
    children: [
      { key: 'templates', label: 'Project Templates', Body: TemplatesTab },
      { key: 'projects', label: 'Projects & Accounts', Body: ProjectsTab },
      { key: 'modules', label: 'Modules', Body: ModulesTab },
      { key: 'budget-categories', label: 'Budget Categories', Body: BudgetCategoriesTab },
    ],
  },
  // No children: nothing to split this one into yet, so the root row is the whole section.
  { key: 'customer-access', label: 'Customer Access', children: [], Body: ExternalUsers },
  {
    key: 'ai-settings', label: 'AI Settings',
    children: [
      { key: 'usage', label: 'Usage', Body: UsageTab },
      { key: 'limits', label: 'Limits', Body: LimitsTab },
      { key: 'prompt', label: 'SOW Extract Prompt', Body: PromptTab },
    ],
  },
];

/**
 * One admin hub instead of four separate pages that each linked to the others (Jeremy, 2026-09-30).
 * Navigated as folder tabs: the active section's tab is drawn attached to the panel below it (no
 * border between them, matching background), and that panel opens with the section's own sub-tabs
 * as a plain underlined row -- "subtasks sit as smaller attached tabs directly under their parent
 * task tab, all in one row group, no nested box" (Jeremy, 2026-09-30, reference mockup). Picking a
 * different section jumps to its first child.
 *
 * Maintenance Mode and Login History deliberately stay separate pages rather than becoming tabs --
 * they're occasional detours from this hub, not sections of it, so they're small links in the corner
 * (ProjectLaunch.js's Project Setup/PM Activity pattern) instead of rows here.
 */
function Administration() {
  const { currentUser } = useApp();
  // Hooks stay unconditional (Rules of Hooks) even though their result is unused on the non-admin
  // branch below -- computing `section`/`subtab` for a role that never sees them is a harmless few
  // extra object lookups, nowhere near worth risking a conditional hook call for.
  const confirmNavigation = useNavGuardConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const sectionKey = activeTabKey(searchParams.get('tab'), TREE);
  const section = TREE.find((s) => s.key === sectionKey);
  const subtabKey = section.children.length
    ? activeTabKey(searchParams.get('subtab'), section.children)
    : null;
  const subtab = subtabKey ? section.children.find((c) => c.key === subtabKey) : null;
  const Body = subtab ? subtab.Body : section.Body;

  if (currentUser && currentUser.role !== 'admin') {
    return (
      <div>
        <div className="page-header">
          <div className="page-eyebrow">Administration</div>
          <h1 className="page-title">Administration</h1>
        </div>
        <div className="card">
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            This page is only available to administrators.
          </div>
        </div>
      </div>
    );
  }

  // Jumping sections lands on its first child (if it has any) -- same as picking a different top
  // folder in a file tree opens to the first item inside it, rather than leaving the old section's
  // sub-page name in the URL where it may not even exist. Re-clicking the already-active section is
  // a no-op rather than bouncing back to its first child -- a stray re-click (focus, misclick)
  // otherwise silently threw away whichever sub-tab was open.
  const selectSection = (key) => {
    if (key === section.key) return;
    if (!confirmNavigation()) return;
    const next = TREE.find((s) => s.key === key);
    setSearchParams(
      next.children.length ? { tab: key, subtab: next.children[0].key } : { tab: key },
      { replace: true },
    );
  };

  const selectSubtab = (key) => {
    if (!confirmNavigation()) return;
    setSearchParams({ tab: section.key, subtab: key }, { replace: true });
  };

  return (
    <div>
      <div
        className="page-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}
      >
        <div>
          <div className="page-eyebrow">Administration</div>
          <h1 className="page-title">Administration</h1>
          <p className="page-subtitle">
            Org structure, project templates, customer access and AI settings — the settings that
            apply across every project, not just one.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, fontSize: 12, paddingTop: 8, whiteSpace: 'nowrap' }}>
          <Link to="/maintenance">Maintenance Mode →</Link>
          <Link to="/login-history">Login History →</Link>
          <Link to="/billing-rates">Billing Rates →</Link>
        </div>
      </div>

      {/* merge only when there's a sub-tab strip to merge into -- Customer Access has none, so its
          tab gets a normal complete border instead of erasing into nothing. Body is NOT wrapped in
          a panel of its own: every Tab component (TeamsTab, TemplatesTab, ExternalUsers, ...)
          already renders its own `.card`, so wrapping it again doubled the border/padding/shadow on
          every single tab in the hub -- caught in review before this shipped. */}
      <TabBar
        tabs={TREE}
        active={section.key}
        onSelect={selectSection}
        merge={section.children.length > 0}
      />

      {section.children.length > 0 && (
        <div
          className="card"
          style={{
            position: 'relative', zIndex: 1, marginBottom: 16,
            display: 'flex', gap: 20, flexWrap: 'wrap', padding: '12px 20px',
          }}
        >
          {section.children.map((c) => {
            const isActiveChild = c.key === subtab?.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => selectSubtab(c.key)}
                style={{
                  fontSize: 13, fontWeight: isActiveChild ? 700 : 500, cursor: 'pointer',
                  padding: '2px 0 6px', border: 'none', background: 'transparent', marginBottom: -1,
                  borderBottom: `2px solid ${isActiveChild ? 'var(--primary-dark)' : 'transparent'}`,
                  color: isActiveChild ? 'var(--text-dark)' : 'var(--text-mid)',
                }}
                aria-pressed={isActiveChild}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      )}

      <Body />
    </div>
  );
}

export default Administration;
